import type { Client } from '@/clients/types';
import { OrbitModule, clientHasModule } from '@/clients/modules';
import { RECORD_FOCUS_ZOOM } from './mapDefaults';
import type { MapSearchParams } from './mapUrlState';
import type { PoiFeatureProperties } from './poiFeatureProperties';

/** Where a hand-off lands: the client's Find & Fix map, on the record. */
export const FIND_AND_FIX_HAND_OFF_ROUTE = '/find-and-fix/network-overview';

export type FindAndFixHandOff =
    /** Switch to `client`, then navigate to the route with `search`. */
    | { kind: 'ok'; client: Client; search: MapSearchParams }
    /** The record names no client the user can see; nothing to open. */
    | { kind: 'client-unavailable' }
    /** The client exists but has no Find & Fix to open the record in. */
    | { kind: 'module-missing'; client: Client };

/**
 * Works out how to open a Network Explorer record in its own client's Find & Fix. The search
 * names the client, so a new tab boots on it (and an in-tab caller switches client FIRST, the
 * switcher's precedent, so the product route mounts on the record's client); it carries the
 * viewport, so the map opens on the record unclustered, and the record's identity, so the
 * Find & Fix map can select it once it has loaded that client's worklist.
 */
export function buildFindAndFixHandOff(
    entity: PoiFeatureProperties,
    coords: [number, number] | null,
    clients: Client[],
): FindAndFixHandOff {
    const client = entity.client_id ? clients.find((c) => c.id === entity.client_id) : undefined;
    if (!client) return { kind: 'client-unavailable' };
    if (!clientHasModule(client, OrbitModule.LeakDetection)) return { kind: 'module-missing', client };

    return {
        kind: 'ok',
        client,
        search: {
            client: client.id,
            lng: coords?.[0],
            lat: coords?.[1],
            z: coords ? RECORD_FOCUS_ZOOM : undefined,
            view: '2d',
            poi: entity.id,
            group: entity.group_id,
        },
    };
}
